import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { debugLog, debugError } from '../utils/debug';

// Enable credentials globally for cookie handling
axios.defaults.withCredentials = true;

const JWT_SESSION_ERROR_CODES = new Set([
	'jwt_auth_obsolete_refresh_token',
	'jwt_auth_invalid_token',
	'jwt_auth_expired_token',
	'jwt_auth_no_auth_header',
	'jwt_auth_bad_auth_header',
	'jwt_auth_invalid_issuer',
]);

const isJwtSessionError = (error: any): boolean => {
	const code = error?.response?.data?.code;
	const message = String(error?.response?.data?.message || '').toLowerCase();
	const requestUrl = String(error?.config?.url || '').toLowerCase();

	if (code && JWT_SESSION_ERROR_CODES.has(code)) {
		return true;
	}

	// Some environments return only message text without a stable code.
	const hasJwtExpiryMessage = message.includes('token') && (message.includes('expired') || message.includes('invalid'));
	if (hasJwtExpiryMessage) {
		return true;
	}

	// Keep refresh endpoint failures as hard session failures.
	return requestUrl.includes('/jwt-auth/v1/token/refresh');
};

// Interceptor do automatycznego czyszczenia localStorage przy błędach JWT
axios.interceptors.response.use(
	(response) => response,
	(error) => {
		if (isJwtSessionError(error)) {
			debugLog('JWT error detected, clearing localStorage');
			localStorage.clear(); // Wyczyść wszystko
			// Można dodać redirect do logowania
			window.location.href = '/login';
		}
		return Promise.reject(error);
	},
);

export const sendLogoutRequest = async () => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/logout' : '';

	try {
		const response = await axios.post(url);
		if (response.status != 200) {
			debugError('Logout response error:', response);
		}
		// Always clear localStorage on logout, regardless of server response
		debugLog('Logout successful, clearing localStorage');
		localStorage.clear();

		// Also clear any cookies that might be set by the browser
		document.cookie.split(';').forEach(function (c) {
			document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
		});

		return;
	} catch (error: any) {
		debugError('Logout request failed:', error.response ? error.response : error);
		// Clear localStorage even if logout request fails
		debugLog('Logout request failed, but clearing localStorage anyway');
		localStorage.clear();

		// Also clear cookies even if logout request fails
		document.cookie.split(';').forEach(function (c) {
			document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
		});
	}
	return null;
};

export const sendLoginRequest = async (
	login: string,
	password: string,
	onError?: (error: string, code?: string) => void,
) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/jwt-auth/v1/token' : '';

	const formData = new FormData();
	formData.append('username', login);
	formData.append('password', password);

	const config = {
		headers: {},
	};
	try {
		const response = await axios.post(url, formData, config);
		const payload = response.data;
		if (payload?.success === false || payload?.code === 'olijp_email_not_verified') {
			const code = payload?.code || '';
			const message =
				payload?.message ||
				'Konto jest nieaktywne. Potwierdź adres email — kliknij link w wiadomości wysłanej po rejestracji.';
			onError && onError(message, code);
			return null;
		}
		if (response.status != 200) {
			onError && onError('Nastąpił błąd przy wysyłaniu formularza! Spróbuj ponownie później.');
		}
		return response.data.data;
	} catch (error: any) {
		console.error(error.response ? error.response : error);

		const code = error.response?.data?.code as string | undefined;
		const message = error.response?.data?.message as string | undefined;

		if (code === 'olijp_email_not_verified') {
			onError &&
				onError(
					message ||
						'Konto jest nieaktywne. Potwierdź adres email — kliknij link w wiadomości wysłanej po rejestracji.',
					code,
				);
			return null;
		}

		if (code === 'jwt_auth_obsolete_refresh_token') {
			debugLog('JWT refresh token obsolete, clearing localStorage');
			localStorage.removeItem('user');
			onError && onError('Sesja wygasła. Zaloguj się ponownie.', code);
		} else if (error.response?.status === 401) {
			debugLog('Unauthorized login attempt');
			onError && onError('Nieprawidłowy login lub hasło', code);
		} else if (error.response?.status === 403) {
			onError && onError('Nieprawidłowy login lub hasło', code);
		} else {
			onError && onError('Nastąpił błąd przy wysyłaniu formularza! Spróbuj ponownie później.');
		}
	}
	return null;
};

export const sendResetLinkRequest = async (email: string, onError?: (error: string) => void) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/reset-link' : '';

	const formData = new FormData();
	formData.append('email', email);

	const config = {
		headers: {},
	};
	try {
		const response = await axios.post(url, formData, config);
		return response.data;
	} catch (error: any) {
		if (error.response) {
			// Zwróć odpowiedź z backendu (może zawierać user_exists: false)
			return error.response.data;
		}
		console.error(error);
		if (onError) {
			onError('Wystąpił błąd przy wysyłaniu linku resetującego hasło. Spróbuj ponownie później.');
		}
	}
	return null;
};

export const sendResetPassword = async (
	login: string,
	key: string,
	password: string,
	onError?: (error: string) => void,
) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/password-reset' : '';

	const config = {
		headers: {},
	};
	try {
		const response = await axios.post(url, { login, key, password }, config);
		if (response.status != 200) {
			console.error(response);
		}
		return response.data;
	} catch (error: any) {
		console.error(error.response ? error.response : error);
		if (onError) {
			onError(error.response ? error.response.data?.message || 'Błąd resetowania hasła' : 'Błąd połączenia');
		}
		return error.response;
	}
};

export const confirmRegistrationEmail = async (key: string, login: string) => {
	const base = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/confirm-email' : '';
	const url = `${base}?key=${encodeURIComponent(key)}&login=${encodeURIComponent(login)}`;
	const response = await axios.get(url);
	return response.data;
};

export const resendConfirmRegistrationEmail = async (email: string) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/resend-confirm-email' : '';
	const response = await axios.post(url, { email });
	return response.data as { message?: string; sent?: boolean; alreadyVerified?: boolean };
};

export const getTeacherRegistrationStatus = async (token: string) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/teacher/status' : '';
	const response = await axios.get(url, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

export const getStudentRegistrationStatus = async (token: string) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/student/status' : '';
	const response = await axios.get(url, {
		headers: { Authorization: `Bearer ${token}` },
	});
	return response.data;
};

export const checkPriorTeacher = async (
	email: string,
	firstName: string,
	lastName: string,
	districtId?: string | number,
) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/teacher/check-prior' : '';
	const response = await axios.post(url, {
		email,
		firstName,
		lastName,
		...(districtId ? { districtId: Number(districtId) } : {}),
	});
	return response.data;
};

export const verifyPriorTeacher = async (email: string, password: string, firstName: string, lastName: string) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/teacher/verify-prior' : '';
	try {
		const response = await axios.post(url, { email, password, firstName, lastName });
		return response.data;
	} catch (error: any) {
		const message = error.response?.data?.message || 'Nieprawidłowy email lub hasło.';
		return { ok: false, error: message };
	}
};

export const checkPriorStudent = async (
	email: string,
	firstName: string,
	lastName: string,
	districtId?: string | number,
) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/student/check-prior' : '';
	const response = await axios.post(url, {
		email,
		firstName,
		lastName,
		...(districtId ? { districtId: Number(districtId) } : {}),
	});
	return response.data;
};

export const verifyPriorStudent = async (email: string, password: string, firstName: string, lastName: string) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/student/verify-prior' : '';
	try {
		const response = await axios.post(url, { email, password, firstName, lastName });
		return response.data;
	} catch (error: any) {
		const message = error.response?.data?.message || 'Nieprawidłowy email lub hasło.';
		return { ok: false, error: message };
	}
};

export type DuplicateNameWarning = {
	maskedEmail: string;
	edition?: number | null;
	role?: string;
};

export const checkDuplicateName = async (params: {
	firstName: string;
	lastName: string;
	districtId: string | number;
	excludeEmail?: string;
	role?: 'teacher' | 'student' | 'both';
}) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/duplicate-name-check' : '';
	const response = await axios.post(url, {
		firstName: params.firstName,
		lastName: params.lastName,
		districtId: Number(params.districtId),
		excludeEmail: params.excludeEmail,
		role: params.role || 'both',
	});
	return response.data as { warnings: DuplicateNameWarning[] };
};

export const registerNewTeacher = async (
	formInfo: {
		personalTitle: string;
		academicDegree: string;
		firstName: string;
		secondName: string;
		lastName: string;
		category: string;
		affiliation: string;
		students: string;
		email: string;
		phone: string;
		city: string;
		street: string;
		zipCode: string;
		building: string;
		apartment: string;
		districtId: string;
		password?: string;
		passwordConfirm?: string;
		noSchoolFacility?: boolean;
		declaredStudents?: { firstName: string; lastName: string }[];
	},
	onError?: (error: string) => void,
) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/teacher' : '';

	const data = JSON.stringify(formInfo);

	const config = {
		headers: {
			'Content-Type': 'application/json',
		},
	};
	try {
		const response = await axios.post(url, data, config);
		return response.data;
	} catch (error: any) {
		console.error(error.response ? error.response : error);
		const message = error.response?.data?.message;
		if (message && onError) {
			onError(message);
		}
		return { error: message || 'Błąd rejestracji' };
	}
};

export const registerNewStudent = async (
	formInfo: {
		teachersIds: any;
		specializationId: string;
		personalTitle: string;
		parentName: string;
		parentEmail: string;
		firstName: string;
		lastName: string;
		email: string;
		phone: string;
		city: string;
		street: string;
		zipCode: string;
		building: string;
		apartment: string;
		districtId: string;
		schoolId: string;
		specialCertificate: boolean;
		password?: string;
		passwordConfirm?: string;
		isStudentUnderage?: boolean;
		class?: number | string;
		secondName?: string;
		birthday?: string;
	},
	onError?: (error: string) => void,
	opinionFiles?: File[] | File | null,
) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/registration/student' : '';

	const files = !opinionFiles ? [] : Array.isArray(opinionFiles) ? opinionFiles : [opinionFiles];

	try {
		let response;
		if (files.length > 0) {
			const body = new FormData();
			body.append('payload', JSON.stringify(formInfo));
			files.forEach((f) => body.append('opinion[]', f));
			response = await axios.post(url, body);
		} else {
			response = await axios.post(url, JSON.stringify(formInfo), {
				headers: { 'Content-Type': 'application/json' },
			});
		}
		if (response.status != 200) {
			console.error(response);
		}
		return response.data;
	} catch (error: any) {
		console.error(error.response ? error.response : error);
		const message = error.response?.data?.message;
		if (message && onError) {
			onError(message);
		}
		return error.response ? error.response.data.message : error;
	}
};

export const registerForeignStudent = async (formInfo: {
	personalTitle: string;
	firstName: string;
	secondName?: string;
	lastName: string;
	email: string;
	phone: string;
	classText: string;
	specializationId: string;
	birthday?: Dayjs | null;
	specialCertificate?: boolean;
	country: string;
	city?: string;
	street?: string;
	building?: string;
	apartment?: string;
	zipCode?: string;
	schoolName: string;
	schoolCity?: string;
	schoolCountry: string;
	schoolStreet?: string;
	schoolBuilding?: string;
	schoolApartment?: string;
	schoolZipCode?: string;
	teacherFirstName?: string;
	teacherLastName?: string;
	teacherEmail?: string;
	teacherPhone?: string;
	teacherAffiliation?: string;
	teacherCity?: string;
	teacherStreet?: string;
	teacherBuilding?: string;
	teacherApartment?: string;
	teacherZipCode?: string;
	password?: string;
	passwordConfirm?: string;
}) => {
	const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + '/foreign-registration/student' : '';
	/** Bez ISO z „Z”: JSON.stringify(Dayjs) używa toISOString() (UTC) i psuje dzień kalendarzowy w PL. */
	const { birthday, ...rest } = formInfo;
	const payload = {
		...rest,
		birthday: birthday ? dayjs(birthday).format('YYYY-MM-DD') : '',
	};
	const data = JSON.stringify(payload);
	const config = {
		headers: {
			'Content-Type': 'application/json',
		},
	};
	try {
		const response = await axios.post(url, data, config);
		if (response.status != 200) {
			console.error(response);
		}
		return response.data;
	} catch (error: any) {
		console.error(error.response ? error.response : error);
		return error.response ? error.response.data.message : error;
	}
};
